import React, { ReactElement } from 'react';

import { Control } from 'react-hook-form';
import { observer } from 'mobx-react';
import { Button, LbInput, LbSelect, LbDatePicker } from '@infoods/lifebase-ui';

import { ComboBox } from '../../../../../shared/components/form-components';
import {
  CheckboxCell,
  GridTableRow,
} from '../../../../../shared/components/table-components';
import { statusOptions } from './GeneralFilters.settings';
import { CustomerCommunicationFilterFormValues } from '../../../../interfaces/CustomerCommunicationFilters';
import { customerCommunicationStore } from '../../../../stores/customerCommunicationStore';
import { useGroupList } from '../../../../../customer-group';
import { CustomFiltersSelector } from '../../../../../shared/features/custom-filter';
import {
  CUSTOMER_COMMUNICATION_COLUMN,
  CustomerCommunicationColumn,
} from '../../NgzCustomerCommunicationList.settings';

type GeneralFiltersProps = {
  control: Control<CustomerCommunicationFilterFormValues>;
  onReset: () => void;
  visibleColumns: CustomerCommunicationColumn[];
};

const GeneralFilters = observer(
  ({ control, onReset, visibleColumns }: GeneralFiltersProps) => {
    const { groupListOptionWithAll } = useGroupList();
    const { selectMethod } = customerCommunicationStore;
    const { getMasterCheckboxProps } = selectMethod;

    const inputGeneralProps = {
      control,
      variant: 'outlined',
      size: 'small',
    } as const;

    const renderColumnsInfo: Record<CustomerCommunicationColumn, ReactElement> = {
      [CUSTOMER_COMMUNICATION_COLUMN.NAME]: (
        <CheckboxCell showCheckbox {...getMasterCheckboxProps()}>
          <LbInput
            {...inputGeneralProps}
            name="generalFilter.fullName"
            placeholder="Name"
            dataTestId="name-input"
          />
        </CheckboxCell>
      ),
      [CUSTOMER_COMMUNICATION_COLUMN.LOCATION]: (
        <LbInput
          {...inputGeneralProps}
          name="generalFilter.entityName"
          placeholder="Location"
          dataTestId="location-input"
        />
      ),
      [CUSTOMER_COMMUNICATION_COLUMN.GROUP]: (
        <ComboBox
          {...inputGeneralProps}
          name="generalFilter.customerGroupId"
          options={groupListOptionWithAll}
          defaultValue=""
          dataTestId="group-input"
        />
      ),
      [CUSTOMER_COMMUNICATION_COLUMN.COACH]: (
        <LbInput
          {...inputGeneralProps}
          name="generalFilter.coachName"
          placeholder="Name"
          dataTestId="coach-input"
        />
      ),
      [CUSTOMER_COMMUNICATION_COLUMN.START_DATE]: (
        <LbDatePicker {...inputGeneralProps} name="generalFilter.startDate" />
      ),
      [CUSTOMER_COMMUNICATION_COLUMN.STATUS]: (
        <LbSelect
          {...inputGeneralProps}
          name="generalFilter.status"
          options={statusOptions}
          displayEmpty
          dataTestId="status-input"
        />
      ),
      [CUSTOMER_COMMUNICATION_COLUMN.CUSTOM_FILTER]: (
        <CustomFiltersSelector {...inputGeneralProps} name="customFilterId" />
      ),
      [CUSTOMER_COMMUNICATION_COLUMN.ACTIONS]: (
        <Button variant="outlined" size="small" type="reset" fullWidth onClick={onReset}>
          Clear All
        </Button>
      ),
    };

    return (
      <GridTableRow variant="filter" component="form">
        {visibleColumns.map((col) => (
          <React.Fragment key={col}>{renderColumnsInfo[col]}</React.Fragment>
        ))}
      </GridTableRow>
    );
  },
);

export { GeneralFilters };
